import { HouseholdMemberActions } from "./HouseholdMemberActions";
import { MemberMinistryMemberships } from "@/features/memberships/MemberMinistryMemberships";
import { useAuth } from "@/features/auth/hooks/useAuth";
import {
  ArrowRight,
  CalendarCheck2,
  Crown,
  UserRound,
  Users,
} from "lucide-react";
import { Link } from "react-router-dom";

import type { HouseholdIntelligenceResponse } from "@/api/householdIntelligence";
import { Card } from "@/components/ui/card";
import type { HouseholdDetail } from "@/types/household";

type Props = {
  household: HouseholdDetail;
  intelligence?: HouseholdIntelligenceResponse;
};

export function HouseholdMembersCard({
  household,
  intelligence,
}: Props) {
  const { churchId } = useAuth();
  const intelligenceByMemberId = new Map(
    intelligence?.members.map((member) => [member.id, member]) ?? []
  );

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-col gap-4 border-b border-slate-800 p-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-indigo-500/15 p-3 text-indigo-400">
            <Users size={22} />
          </div>

          <div>
            <h2 className="text-xl font-semibold text-white">
              Household Members
            </h2>

            <p className="text-sm text-slate-500">
              People connected to this household
            </p>
          </div>
        </div>

        <span className="w-fit rounded-full bg-indigo-500/15 px-3 py-1 text-sm font-semibold text-indigo-300">
          {household.members.length} member
          {household.members.length === 1 ? "" : "s"}
        </span>
      </div>

      {household.members.length === 0 ? (
        <div className="p-6">
          <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-950/40 p-8 text-center">
            <UserRound
              className="mx-auto text-slate-500"
              size={28}
            />

            <p className="mt-3 font-medium text-white">
              No household members
            </p>

            <p className="mt-2 text-sm text-slate-400">
              Assign members to this household to build its profile.
            </p>
          </div>
        </div>
      ) : (
        <div className="divide-y divide-slate-800">
          {household.members.map((member) => {
            const memberIntelligence =
              intelligenceByMemberId.get(member.id);

            return (
              <div key={member.id} className="px-6 py-5">
              <Link
                to={`/members/${member.id}`}
                className="group flex flex-col gap-4 transition hover:bg-slate-800/40 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex min-w-0 items-center gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 font-bold text-white">
                    {getInitials(
                      member.first_name,
                      member.last_name
                    )}
                  </div>

                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="truncate font-semibold text-white">
                        {member.first_name} {member.last_name}
                      </p>

                      {member.is_head_of_household && (
                        <span className="flex items-center gap-1 rounded-full bg-amber-500/15 px-2.5 py-1 text-xs font-semibold text-amber-300">
                          <Crown size={13} />
                          Head
                        </span>
                      )}
                    </div>

                    <p className="mt-1 text-sm text-slate-500">
                      {member.relationship_to_household ||
                        "Household member"}
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-5 sm:justify-end">
                  <div className="text-left sm:text-right">
                    {memberIntelligence?.last_attended_at ? (
                      <>
                        <div className="flex items-center gap-2 text-sm font-medium text-emerald-300 sm:justify-end">
                          <CalendarCheck2 size={15} />
                          Last attended
                        </div>

                        <p className="mt-1 text-xs text-slate-500">
                          {formatDate(
                            memberIntelligence.last_attended_at
                          )}
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-sm font-medium text-amber-300">
                          No attendance recorded
                        </p>

                        <p className="mt-1 text-xs text-slate-500">
                          Attendance history unavailable
                        </p>
                      </>
                    )}
                  </div>

                  <ArrowRight
                    size={18}
                    className="shrink-0 text-slate-600 transition group-hover:translate-x-1 group-hover:text-indigo-400"
                  />
                </div>
              </Link>
              <HouseholdMemberActions household={household} member={member} />
              {churchId && <MemberMinistryMemberships churchId={churchId} memberId={member.id} memberName={`${member.first_name} ${member.last_name}`} compact />}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function getInitials(firstName: string, lastName: string) {
  return `${firstName[0] ?? ""}${lastName[0] ?? ""}`.toUpperCase();
}

function formatDate(value: string) {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

